import React, { useEffect, useState } from "react";
import { HelpCircle, X, ArrowLeft, ArrowRight, Crosshair, ChevronDown, List, Search, Check, MessageSquare, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { glowTarget } from "../lib/guideGlow";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTips, TipIcon } from "./CoachTip";
import { PROTON_AFFILIATE_URL } from "../lib/proton";
import { BlueprintDownload } from "./BlueprintDownload";

const TOUR_KEY = "beacon-guide-tour";
const LINKS = { proton: PROTON_AFFILIATE_URL };
const Linked = ({ text }) => <>{String(text).split(/(\[[^\]]+\]\([a-z]+\))/g).map((seg, i) => {
  const m = seg.match(/^\[([^\]]+)\]\(([a-z]+)\)$/);
  return m && LINKS[m[2]] ? <a key={i} href={LINKS[m[2]]} target="_blank" rel="noopener noreferrer" data-testid={`guide-link-${m[2]}`} className="text-[#14A89A] underline hover:brightness-125">{m[1]}</a> : <React.Fragment key={i}>{seg}</React.Fragment>;
})}</>;
const Rich = ({ text }) => <>{String(text || "").split("**").map((s, i) => (i % 2 ? <span key={i} className="text-white font-medium"><Linked text={s} /></span> : <Linked key={i} text={s} />))}</>;

const showMe = (target, close) => {
  close();
  glowTarget(target, { missing: "This part appears once it's set up. It lives further down your builder." });
};

const ShowMeBtn = ({ t, close }) => t.target ? (
  <button onClick={() => showMe(t.target, close)} data-testid={`guide-showme-${t.tip_id}`} className="inline-flex items-center gap-1.5 border border-[#14A89A]/50 text-[#14A89A] px-3 py-1.5 font-body text-xs hover:bg-[#14A89A]/10 transition-colors">
    <Crosshair size={13} /> Show me
  </button>
) : null;

const Tour = ({ topics, close, toList }) => {
  const [i, setI] = useState(0);
  const t = topics[i];
  const last = i === topics.length - 1;
  return (
    <div data-testid="beacon-guide-tour">
      <div className="h-0.5 bg-white/10 mb-5"><div className="h-full bg-[#14A89A] transition-all duration-300" style={{ width: `${((i + 1) / topics.length) * 100}%` }} /></div>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-lg bg-[#14A89A]/15 text-[#14A89A] flex items-center justify-center"><TipIcon name={t.icon} size={18} /></span>
        <span className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500">Step {i + 1} of {topics.length}</span>
      </div>
      <h3 className="font-heading text-xl text-white mb-2" data-testid="guide-tour-title">{t.title}</h3>
      <p className="font-body text-sm text-zinc-400 leading-relaxed mb-5"><Rich text={t.body} /></p>
      <ShowMeBtn t={t} close={close} />
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
        <button onClick={() => setI(i - 1)} disabled={i === 0} data-testid="guide-tour-back" className="inline-flex items-center gap-1.5 font-body text-sm text-zinc-400 hover:text-white disabled:opacity-30"><ArrowLeft size={15} /> Back</button>
        <button onClick={toList} data-testid="guide-tour-all" className="inline-flex items-center gap-1 font-body text-xs text-zinc-500 hover:text-white"><List size={13} /> All topics</button>
        <button onClick={() => (last ? close() : setI(i + 1))} data-testid="guide-tour-next" className="inline-flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-sm px-4 py-2 hover:brightness-110">
          {last ? "Done" : <>Next <ArrowRight size={15} /></>}
        </button>
      </div>
    </div>
  );
};

// Empty search → send the question straight to the Council inbox (inquiry, not E2E), prefilled.
const AskCouncil = ({ q }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [state, setState] = useState("idle");
  const start = () => { setMsg(`Beacon Guide question: I searched for "${q.trim()}" and couldn't find an answer.\n\n`); setOpen(true); };
  const send = async () => {
    if (!msg.trim()) return;
    setState("busy");
    try { await api.post("/council-messages", { kind: "inquiry", message: msg.trim(), sender_name: user?.name }); setState("sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send, please try again"); setState("idle"); }
  };
  if (state === "sent") return (
    <div className="py-6 text-center" data-testid="guide-ask-sent">
      <p className="font-body text-sm text-white flex items-center justify-center gap-1.5"><Check size={15} className="text-[#14A89A]" /> Sent to the Council</p>
      <p className="font-body text-xs text-zinc-500 mt-1">Replies arrive in <Link to="/council/messages" className="text-[#14A89A] hover:underline">your Council messages</Link>.</p>
    </div>
  );
  return (
    <div className="py-5" data-testid="guide-search-empty">
      <p className="font-body text-sm text-zinc-500 text-center">No topics match "{q}". Try another word, or ask us directly.</p>
      {!open
        ? <div className="flex justify-center mt-3"><button onClick={start} data-testid="guide-ask-council" className="inline-flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-xs px-4 py-2 hover:brightness-110"><MessageSquare size={14} /> Ask the Council</button></div>
        : <div className="mt-3 space-y-2 fade-up">
            <textarea autoFocus value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} maxLength={2000} data-testid="guide-ask-message"
              className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#14A89A] px-3 py-2 text-white font-body text-sm outline-none resize-none" />
            <p className="font-body text-[11px] text-zinc-600">Goes to the Council's inbox as a regular question, readable by stewards (not end-to-end encrypted).</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="font-body text-xs text-zinc-400 px-3 py-1.5">Cancel</button>
              <button onClick={send} disabled={state === "busy" || !msg.trim()} data-testid="guide-ask-send" className="bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-xs px-4 py-1.5 disabled:opacity-40">{state === "busy" ? "Sending…" : "Send"}</button>
            </div>
          </div>}
    </div>
  );
};

const Topic = ({ t, open, setOpen, expanded, isNew, close }) => (
  <div data-testid={`guide-topic-${t.tip_id}`}>
    <button onClick={() => setOpen(open === t.tip_id ? null : t.tip_id)} aria-expanded={expanded} data-testid={`guide-topic-toggle-${t.tip_id}`} className="w-full flex items-center gap-3 py-3 text-left group">
      <TipIcon name={t.icon} size={16} className="text-[#14A89A] shrink-0" />
      <span className="flex-1 font-body text-sm text-white group-hover:text-[#14A89A] transition-colors">{t.title}</span>
      {isNew(t) && <span className="font-body text-[9px] font-semibold uppercase tracking-wider bg-amber-300 text-[#0A0A0A] px-1.5 py-0.5" data-testid={`guide-new-${t.tip_id}`}>New</span>}
      <ChevronDown size={15} className={`text-zinc-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded && (
      <div className="pl-7 pb-4 fade-up">
        <p className="font-body text-sm text-zinc-400 leading-relaxed mb-3"><Rich text={t.body} /></p>
        <ShowMeBtn t={t} close={close} />
      </div>
    )}
  </div>
);

const Chapter = ({ c, isOpen, toggle, children, isNew }) => {
  const fresh = c.topics.filter(isNew).length;
  return (
    <div className="border-b border-white/10" data-testid={`guide-chapter-${c.key}`}>
      <button onClick={toggle} aria-expanded={isOpen} data-testid={`guide-chapter-toggle-${c.key}`} className="w-full flex items-center gap-2 py-3 text-left group">
        <span className="flex-1 font-body text-sm font-semibold text-white group-hover:text-[#14A89A] transition-colors">{c.label}</span>
        {fresh > 0 && <span className="font-body text-[9px] font-semibold uppercase tracking-wider bg-amber-300 text-[#0A0A0A] px-1.5 py-0.5">{fresh} new</span>}
        <span className="font-body text-[11px] text-zinc-500">{c.topics.length}</span>
        <ChevronDown size={15} className={`text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && <div className="divide-y divide-white/5 border-t border-white/5 pl-1 mb-2 fade-up">{children}</div>}
    </div>
  );
};

const TopicList = ({ topics, chapters, close, startTour, isNew, tplTour }) => {
  const [open, setOpen] = useState(null);
  const [openCh, setOpenCh] = useState(() => new Set([chapters[0]?.key]));
  const toggleCh = (k) => setOpenCh((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [q, setQ] = useState("");
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = terms.length ? topics.filter((t) => { const hay = `${t.title} ${t.body}`.toLowerCase().replace(/\*\*/g, ""); return terms.every((w) => hay.includes(w)); }) : topics;
  const renderTopics = (list) => list.map((t) => <Topic key={t.tip_id} t={t} open={open} setOpen={setOpen} close={close} isNew={isNew}
    expanded={open === t.tip_id || (terms.length > 0 && shown.length <= 2)} />);
  return (
    <div data-testid="beacon-guide-list">
      <button onClick={startTour} data-testid="guide-start-tour" className="w-full flex items-center gap-3 border border-[#14A89A]/40 bg-[#14A89A]/[0.07] hover:bg-[#14A89A]/[0.14] transition-colors p-3 mb-4 text-left">
        <PlayCircle size={22} className="text-[#14A89A] shrink-0" />
        <span className="flex-1"><span className="block font-body text-sm font-semibold text-white">Take the guided tour</span>
          <span className="block font-body text-xs text-zinc-400">A quick walk through the basics, one step at a time</span></span>
        <ArrowRight size={15} className="text-[#14A89A]" />
      </button>
      {tplTour && <button onClick={tplTour.start} data-testid="guide-start-template-tour" className="-mt-2 mb-4 font-body text-xs text-[#14A89A] hover:underline flex items-center gap-1.5"><PlayCircle size={13} /> Or take the {tplTour.label} tour</button>}
      <BlueprintDownload testid="guide-blueprint-download" className="mb-4" />
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the guide, e.g. comments, Stripe, replay" data-testid="guide-search"
          className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#14A89A] pl-9 pr-8 py-2.5 text-white font-body text-sm outline-none" />
        {q && <button onClick={() => setQ("")} aria-label="Clear search" data-testid="guide-search-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"><X size={14} /></button>}
      </div>
      {shown.length === 0 && <AskCouncil q={q} />}
      {terms.length
        ? <div className="divide-y divide-white/10 border-y border-white/10" data-testid="guide-chapter-results">{renderTopics(shown)}</div>
        : chapters.map((c) => c.topics.length > 0 && (
          <React.Fragment key={c.key}>
            {c.heading && <div className="font-body text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-4 mb-1" data-testid={`guide-heading-${c.key}`}>{c.heading}</div>}
            <Chapter c={c} isOpen={openCh.has(c.key)} toggle={() => toggleCh(c.key)} isNew={isNew}>{renderTopics(c.topics)}</Chapter>
          </React.Fragment>
        ))}
    </div>
  );
};

// Topics the creator has already seen (id@rev), per device; first visit counts everything as seen.
const seenKey = (uid) => `ss_guide_seen_${uid}`;
const readSeen = (uid) => { try { return JSON.parse(localStorage.getItem(seenKey(uid))); } catch { return null; } };
const TPL_CHAPTERS = [{ key: "artist", label: "Artist" }, { key: "teacher", label: "Teacher" }];
const BASIC_GROUPS = [
  { key: "getting-started", label: "Getting started", ids: ["guide-start", "guide-look", "guide-welcome", "guide-dashboard", "guide-milestones"] },
  { key: "content", label: "Your content", ids: ["guide-content", "guide-comments", "guide-teaser", "guide-share"] },
  { key: "hearth", label: "Your Hearth", ids: ["guide-hearth", "guide-sessions", "guide-fireside", "guide-recaps"] },
  { key: "money", label: "Members and money", ids: ["guide-pricing", "guide-founding"] },
  { key: "privacy", label: "Privacy and backup", ids: ["guide-privacy", "guide-backup"] },
];
const topicKey = (t) => `${t.tip_id}@${t.rev}`;
// Topics that shipped with the first guide, so creators who already used it see later additions as New.
const LAUNCH_TOPICS = ["guide-start", "guide-look", "guide-welcome", "guide-content", "guide-comments", "guide-teaser", "guide-pricing",
  "guide-hearth", "guide-dashboard", "guide-milestones", "guide-privacy", "guide-backup", "guide-founding"];

// "Beacon Guide" button for the owner's builder; opens the tour automatically the first time.
export const BeaconGuide = ({ template }) => {
  const { user, dismissTip } = useAuth();
  const { tips } = useTips();
  const all = tips.filter((t) => t.screen === "beacon-guide").sort((a, b) => a.priority - b.priority);
  const general = all.filter((t) => !t.chapter);
  const byTpl = { artist: all.filter((t) => t.chapter === "artist"), teacher: all.filter((t) => t.chapter === "teacher") };
  const tplChapters = TPL_CHAPTERS.map((c) => ({ ...c, label: `${c.label} template`, topics: byTpl[c.key] }));
  const mine = tplChapters.find((c) => c.key === template);
  const others = tplChapters.filter((c) => c.key !== template).map((c, i) => ({ ...c, heading: i === 0 ? "Other templates" : null }));
  const grouped = new Set(BASIC_GROUPS.flatMap((g) => g.ids));
  const basics = [...BASIC_GROUPS.map((g) => ({ ...g, topics: g.ids.map((id) => general.find((t) => t.tip_id === id)).filter(Boolean) })),
    { key: "more", label: "More", topics: general.filter((t) => !grouped.has(t.tip_id)) }]
    .filter((g) => g.topics.length).map((g, i) => ({ ...g, heading: i === 0 ? "The basics, for every Beacon" : null }));
  const chapters = mine ? [{ ...mine, heading: "Your template" }, ...basics, ...others] : [...basics, ...others];
  const topics = chapters.flatMap((c) => c.topics);
  const [mode, setMode] = useState(null);
  const [tourTopics, setTourTopics] = useState(null);
  useEffect(() => {
    const go = (e) => { const t = byTpl[e.detail] || []; if (t.length) { setTourTopics(t.slice(0, 4)); setMode("tour"); } };
    window.addEventListener("ss-template-tour", go);
    const openList = () => setMode("list");
    window.addEventListener("ss-open-guide", openList);
    return () => { window.removeEventListener("ss-template-tour", go); window.removeEventListener("ss-open-guide", openList); };
  }, [all.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [seen, setSeen] = useState(null);
  const tourSeen = (user?.dismissed_tips || []).includes(`${TOUR_KEY}@1`);
  useEffect(() => { if (topics.length && user && !tourSeen && !window.__ssTourPaused) setMode("tour"); }, [topics.length, user, tourSeen]);
  useEffect(() => {
    if (!user || !topics.length || seen) return;
    const s = readSeen(user.id);
    if (s) setSeen(s);
    else { const all = topics.filter((t) => !tourSeen || LAUNCH_TOPICS.includes(t.tip_id)).map(topicKey); localStorage.setItem(seenKey(user.id), JSON.stringify(all)); setSeen(all); }
  }, [user, topics, seen, tourSeen]);
  const isNew = (t) => !t.chapter && !!seen && !seen.includes(topicKey(t));
  const newCount = topics.filter(isNew).length;
  const close = () => {
    setMode(null); setTourTopics(null);
    if (!tourSeen) dismissTip(TOUR_KEY, 1);
    const all = topics.map(topicKey);
    localStorage.setItem(seenKey(user.id), JSON.stringify(all));
    setSeen(all);
  };
  if (!topics.length) return null;
  return (
    <>
      <button onClick={() => setMode("list")} data-testid="beacon-guide-btn" title="Beacon Guide" className="relative flex items-center gap-1.5 border border-[#14A89A]/40 text-[#14A89A] px-3 py-2 font-body text-xs hover:bg-[#14A89A]/10 transition-colors">
        <HelpCircle size={14} /> Beacon Guide
        {newCount > 0 && <span className="absolute -top-2 -right-2 bg-amber-300 text-[#0A0A0A] font-semibold text-[9px] leading-none px-1.5 py-1 rounded-full" data-testid="beacon-guide-new-count">{newCount} new</span>}
      </button>
      {mode && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close} data-testid="beacon-guide">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#121212] border border-white/10 w-full sm:max-w-lg max-h-[88vh] overflow-y-auto p-6 relative fade-up">
            <button onClick={close} aria-label="Close guide" data-testid="beacon-guide-close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18} /></button>
            <div className="font-body text-[10px] tracking-[0.25em] uppercase text-[#14A89A] mb-4 flex items-center gap-1.5"><HelpCircle size={12} /> Beacon Guide</div>
            {mode === "tour"
              ? <Tour key={tourTopics ? "template" : "all"} topics={tourTopics || general} close={close} toList={() => { setTourTopics(null); setMode("list"); }} />
              : <TopicList topics={topics} chapters={chapters} close={close} startTour={() => setMode("tour")} isNew={isNew}
                  tplTour={mine?.topics.length ? { label: mine.label, start: () => { setTourTopics(mine.topics.slice(0, 4)); setMode("tour"); } } : null} />}
          </div>
        </div>
      )}
    </>
  );
};
